import "./App.css";
import { ThemeProvider } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useMemo, useRef, useState } from "react";
import { FadeViewTransition } from "./components/FadeViewTransition";
import { ConfiguratorTemplate } from "./components/ConfiguratorTemplate";
import {
  VehicleModelData,
  VehicleModelTemplate,
} from "./components/VehicleModelTemplate";
import { IMenu } from "./UI/IMenu";
import { HorizontalTabsMenu } from "./components/HorizontalTabsMenu";
import { useMenuStore } from "./store/MenuStore";
import { SignInGate } from "./components/SignInGate";
import View360, { type View360Handle } from "./components/View360";
import ViewCurated from "./components/ViewCurated";
import { PortraitOrientationMessage } from "./components/PortraitOrientationMessage";
import {
  buildExterior360Config,
  buildInteriorCuratedConfig,
} from "./util/renderOptions";
import { muiTheme } from "./theme/main";

const vehicleModels: VehicleModelData[] = [
  {
    image: "/assets/Models/21C.png",
    logo: "/assets/Logos/21C Logo.png",
    name: "21C",
    initialFrame: 6,
    infoBox: {
      title: "21C",
      points: [
        "Hybrid hypercar configuration.",
        "Track-focused aero and exterior personalization.",
        "Supports exposed carbon, paint, accents, and pinstripe options.",
      ],
    },
  },
  {
    image: "/assets/Models/Spyder.png",
    logo: "/assets/Logos/Spyder Logo.png",
    name: "21C Spyder",
    initialFrame: 6,
    infoBox: {
      title: "21C Spyder",
      points: [
        "Bespoke configuration starting point.",
        "Designed for custom paint and trim exploration.",
        "Useful for previewing wider personalization combinations.",
      ],
    },
  },
  {
    image: "/assets/Models/VMax.png",
    logo: "/assets/Logos/VMax Logo.png",
    name: "21C VMax",
    initialFrame: 6,
    infoBox: {
      title: "21C VMax",
      points: [
        "High-speed exterior package.",
        "Longtail-inspired silhouette and reduced drag setup.",
        "Uses the same configurator flow with model-specific defaults.",
      ],
    },
  },
];

function App() {
  const [activeView, setActiveView] = useState("models");
  const [loading, setLoading] = useState(false);

  const viewerRef = useRef<HTMLDivElement>(null);
  const view360Ref = useRef<View360Handle>(null);
  const shouldShowPortraitMessage = useMediaQuery(
    "(orientation: portrait) and (max-width: 1199.95px) and (pointer: coarse)",
  );
  const activeMenuId = useMenuStore((state) => state.activeMenuId);

  const exteriorMenuData = useMenuStore(
    (state) => state.allMenuDataById.exterior,
  );

  const interiorMenuData = useMenuStore(
    (state) => state.allMenuDataById.interior,
  );

  const configSlots = useMenuStore((state) => state.configSlots);

  const setSelectedModelName = useMenuStore(
    (state) => state.setSelectedModelName,
  );

  const setActiveMenuId = useMenuStore((state) => state.setActiveMenuId);

  const exterior360Config = useMemo(
    () => buildExterior360Config(exteriorMenuData, configSlots),
    [exteriorMenuData, configSlots],
  );

  const interiorCuratedConfig = useMemo(
    () => buildInteriorCuratedConfig(interiorMenuData, configSlots),
    [interiorMenuData, configSlots],
  );

  const isExteriorViewVisible =
    activeMenuId === "exterior" || activeMenuId === "finalize";

  const selectMenu = (menuId: string) => {
    if (menuId === "finalize") {
      setActiveMenuId(menuId);
      return;
    }

    if (menuId === "models") {
      setSelectedModelName(undefined);
      setActiveView("models");
      return;
    }

    if (menuId === "exterior" || menuId === "interior") {
      setActiveMenuId(menuId);
    }
  };

  return (
    <ThemeProvider theme={muiTheme}>
      {shouldShowPortraitMessage ? (
        <PortraitOrientationMessage />
      ) : (
        <SignInGate>
          <FadeViewTransition
            activeKey={activeView}
            views={[
              {
                key: "models",
                children: (
                  <VehicleModelTemplate
                    models={vehicleModels}
                    loading={loading}
                    onStartConfigurator={(model) => {
                      setSelectedModelName(model.infoBox.title);
                      console.log(model.infoBox.title);
                      view360Ref.current?.setFrame(model.initialFrame);
                      setLoading(true);
                      setTimeout(() => {
                        setActiveView("configurator");
                        setLoading(false);
                      }, 1500);
                    }}
                  />
                ),
              },
              {
                key: "configurator",
                children: (
                  <div className="App">
                    <header className="App-header">
                      <ConfiguratorTemplate
                        optionsMenu={<IMenu />}
                        viewer={
                          <div
                            ref={viewerRef}
                            style={{
                              position: "relative",
                              height: "100%",
                              width: "100%",
                            }}
                          >
                            <div
                              style={{
                                position: "absolute",
                                inset: 0,
                                opacity: isExteriorViewVisible ? 1 : 0,
                                visibility: isExteriorViewVisible
                                  ? "visible"
                                  : "hidden",
                                pointerEvents: isExteriorViewVisible
                                  ? "auto"
                                  : "none",
                                transition: "opacity 180ms ease",
                              }}
                            >
                              <View360
                                ref={view360Ref}
                                config={exterior360Config}
                              ></View360>
                            </div>
                            <div
                              style={{
                                position: "absolute",
                                inset: 0,
                                opacity: activeMenuId === "interior" ? 1 : 0,
                                visibility:
                                  activeMenuId === "interior"
                                    ? "visible"
                                    : "hidden",
                                pointerEvents:
                                  activeMenuId === "interior" ? "auto" : "none",
                                transition: "opacity 180ms ease",
                              }}
                            >
                              <ViewCurated
                                config={interiorCuratedConfig}
                              ></ViewCurated>
                            </div>
                          </div>
                        }
                        navigationMenu={
                          <HorizontalTabsMenu
                            items={[
                              { id: "models", title: "Models" },
                              { id: "exterior", title: "Exterior" },
                              { id: "interior", title: "Interior" },
                              { id: "finalize", title: "Finalize" },
                            ]}
                            activeItemId={activeMenuId}
                            onChange={(item) => selectMenu(item.id)}
                          />
                        }
                        layout="horizontal"
                      ></ConfiguratorTemplate>
                    </header>
                  </div>
                ),
              },
            ]}
          />
        </SignInGate>
      )}
    </ThemeProvider>
  );
}

export default App;
